import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Briefcase, CheckCircle, ArrowRight, ArrowLeft, Sparkles, MapPin, GraduationCap, Plus, X } from 'lucide-react';

const STEPS = [
  { id: 'org', label: 'Your Institution', icon: Building2 },
  { id: 'job', label: 'First Job Post', icon: Briefcase },
  { id: 'done', label: 'All Set', icon: CheckCircle },
];

const EmployerOnboarding = ({ onComplete }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [constants, setConstants] = useState(null);

  const [orgData, setOrgData] = useState({
    name: '', institution_type: '', address: '', website: '', description: '', established_year: '',
  });

  const [jobData, setJobData] = useState({
    title: '', description: '', location: '', job_type: 'full_time', salary_min: '', salary_max: '',
    experience_years: '', job_category: '', subjects: [], grade_levels: [], board_curriculum: [],
  });
  const [subjectInput, setSubjectInput] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  useEffect(() => {
    // Fetch org data and constants
    Promise.all([
      axios.get(`${API}/tenant/current`, { headers, withCredentials: true }).catch(() => null),
      axios.get(`${API}/education/constants`).catch(() => null),
    ]).then(([orgRes, constRes]) => {
      if (orgRes?.data) {
        const o = orgRes.data;
        setOrgData(prev => ({
          ...prev, name: o.name || '', institution_type: o.institution_type || '',
          address: o.address || '', website: o.website || '', description: o.description || '',
          established_year: o.established_year || '',
        }));
      }
      if (constRes?.data) setConstants(constRes.data);
    });
  }, []);

  const updateOrg = (k, v) => setOrgData(p => ({ ...p, [k]: v }));
  const updateJob = (k, v) => setJobData(p => ({ ...p, [k]: v }));

  const addSubject = () => {
    const s = subjectInput.trim();
    if (s && !jobData.subjects.includes(s)) {
      updateJob('subjects', [...jobData.subjects, s]);
      setSubjectInput('');
    }
  };

  const toggleArrayItem = (field, item) => {
    const arr = jobData[field] || [];
    updateJob(field, arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item]);
  };

  const saveOrgStep = async () => {
    if (!orgData.name.trim()) { toast.error('Institution name is required'); return false; }
    setSaving(true);
    try {
      await axios.put(`${API}/tenant/current`, orgData, { headers, withCredentials: true });
      return true;
    } catch { toast.error('Failed to save'); return false; }
    finally { setSaving(false); }
  };

  const saveJobStep = async () => {
    if (!jobData.title.trim()) { toast.error('Job title is required'); return false; }
    if (!jobData.location.trim()) { toast.error('Location is required'); return false; }
    setSaving(true);
    try {
      // Clean up empty string values to null for numeric fields
      const cleanedJobData = {
        ...jobData,
        salary_min: jobData.salary_min === '' ? null : jobData.salary_min,
        salary_max: jobData.salary_max === '' ? null : jobData.salary_max,
        experience_years: jobData.experience_years === '' ? null : jobData.experience_years,
      };
      const res = await axios.post(`${API}/jobs`, cleanedJobData, { headers, withCredentials: true });
      // Publish it immediately
      if (res.data?.job_id) {
        await axios.put(`${API}/jobs/${res.data.job_id}/status?status=published`, {}, { headers, withCredentials: true });
      }
      return true;
    } catch (e) {
      // Handle Pydantic validation errors (array of objects) vs simple string errors
      const detail = e.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(d => d.msg || d).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to post job');
      toast.error(errorMsg);
      return false;
    }
    finally { setSaving(false); }
  };

  const nextStep = async () => {
    let ok = true;
    if (step === 0) ok = await saveOrgStep();
    if (step === 1) ok = await saveJobStep();
    if (ok && step < STEPS.length - 1) setStep(step + 1);
  };

  const finishOnboarding = () => {
    localStorage.setItem('employer_onboarding_done', 'true');
    // Notify backend to send congratulations email
    axios.post(`${API}/tenant/onboarding-complete`, {}, { headers, withCredentials: true }).catch(() => {});
    toast.success('Your first job is live!');
    onComplete();
  };

  const cats = constants?.job_categories ? Object.keys(constants.job_categories) : [];
  const gradeLevels = constants?.grade_levels || [];
  const boards = constants?.board_curriculum || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="employer-onboarding">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-slate-900 text-white px-6 py-5">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>Get Started with EdAssist</h2>
          <p className="text-sm text-slate-400 mt-1">Set up your institution and post your first job in minutes</p>
          <div className="flex items-center gap-1 mt-4">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                  i < step ? 'bg-green-500 text-white' : i === step ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400'
                }`}>
                  {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`w-12 h-0.5 mx-1 ${i < step ? 'bg-green-500' : 'bg-slate-700'}`} />}
              </div>
            ))}
            <span className="text-xs text-slate-400 ml-2">{STEPS[step].label}</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* Step 0: Org Setup */}
          {step === 0 && (
            <div className="space-y-4" data-testid="employer-step-org">
              <div>
                <Label>Institution Name *</Label>
                <Input value={orgData.name} onChange={e => updateOrg('name', e.target.value)} placeholder="e.g. Delhi Public School" className="mt-1" data-testid="employer-org-name" />
              </div>
              <div>
                <Label>Type of Institution</Label>
                <Select value={orgData.institution_type} onValueChange={v => updateOrg('institution_type', v)}>
                  <SelectTrigger className="mt-1" data-testid="employer-org-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {(constants?.institution_types || ['School', 'College', 'University', 'Coaching Center', 'EdTech', 'NGO', 'Other']).map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Address / Location</Label>
                <Input value={orgData.address} onChange={e => updateOrg('address', e.target.value)} placeholder="Full address" className="mt-1" data-testid="employer-org-address" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Website</Label>
                  <Input value={orgData.website} onChange={e => updateOrg('website', e.target.value)} placeholder="https://..." className="mt-1" />
                </div>
                <div>
                  <Label>Established Year</Label>
                  <Input value={orgData.established_year} onChange={e => updateOrg('established_year', e.target.value)} placeholder="e.g. 1990" className="mt-1" />
                </div>
              </div>
              <div>
                <Label>About Your Institution</Label>
                <Textarea value={orgData.description} onChange={e => updateOrg('description', e.target.value)} placeholder="Brief description of your institution..." rows={3} className="mt-1" />
              </div>
            </div>
          )}

          {/* Step 1: First Job Post */}
          {step === 1 && (
            <div className="space-y-4" data-testid="employer-step-job">
              <div className="bg-indigo-50 rounded-lg p-3 mb-2">
                <p className="text-sm text-indigo-700 font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Post your first job — it's free and takes 2 minutes
                </p>
              </div>

              <div>
                <Label>Job Title *</Label>
                <Input value={jobData.title} onChange={e => updateJob('title', e.target.value)} placeholder="e.g. PGT Mathematics Teacher" className="mt-1" data-testid="employer-job-title" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Location *</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={jobData.location} onChange={e => updateJob('location', e.target.value)} placeholder="City" className="pl-9" data-testid="employer-job-location" />
                  </div>
                </div>
                <div>
                  <Label>Job Type</Label>
                  <Select value={jobData.job_type} onValueChange={v => updateJob('job_type', v)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full Time</SelectItem>
                      <SelectItem value="part_time">Part Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {cats.length > 0 && (
                <div>
                  <Label>Category</Label>
                  <Select value={jobData.job_category} onValueChange={v => updateJob('job_category', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Subjects</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={subjectInput} onChange={e => setSubjectInput(e.target.value)} placeholder="e.g. Mathematics" className="flex-1"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubject(); } }} data-testid="employer-subject-input" />
                  <Button variant="outline" size="sm" onClick={addSubject}><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {jobData.subjects.map(s => (
                    <Badge key={s} variant="secondary" className="gap-1 cursor-pointer hover:bg-red-100" onClick={() => updateJob('subjects', jobData.subjects.filter(x => x !== s))}>
                      {s} <X className="w-3 h-3" />
                    </Badge>
                  ))}
                </div>
              </div>

              {gradeLevels.length > 0 && (
                <div>
                  <Label>Grade Levels</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {gradeLevels.slice(0, 10).map(g => (
                      <Badge key={g} variant={jobData.grade_levels.includes(g) ? 'default' : 'outline'}
                        className="cursor-pointer" onClick={() => toggleArrayItem('grade_levels', g)}>
                        {g}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Salary (₹/year)</Label>
                  <Input type="number" value={jobData.salary_min} onChange={e => updateJob('salary_min', e.target.value ? Number(e.target.value) : '')} placeholder="e.g. 300000" className="mt-1" />
                </div>
                <div>
                  <Label>Max Salary (₹/year)</Label>
                  <Input type="number" value={jobData.salary_max} onChange={e => updateJob('salary_max', e.target.value ? Number(e.target.value) : '')} placeholder="e.g. 600000" className="mt-1" />
                </div>
              </div>

              <div>
                <Label>Job Description</Label>
                <Textarea value={jobData.description} onChange={e => updateJob('description', e.target.value)} placeholder="Describe the role, responsibilities, and requirements..." rows={4} className="mt-1" data-testid="employer-job-desc" />
              </div>
            </div>
          )}

          {/* Step 2: Done */}
          {step === 2 && (
            <div className="text-center py-8" data-testid="employer-step-done">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>You're All Set!</h3>
              <p className="text-slate-500 max-w-md mx-auto mb-4">
                Your first job posting is now live. Qualified educators will start applying soon. You can manage applications from your dashboard.
              </p>
              <div className="flex flex-col gap-2 max-w-xs mx-auto mt-6">
                <Button onClick={() => { finishOnboarding(); navigate('/jobs'); }} variant="outline" data-testid="employer-view-jobs-btn">
                  <Briefcase className="w-4 h-4 mr-2" /> View My Jobs
                </Button>
                <Button onClick={() => { finishOnboarding(); navigate('/jobs/create'); }} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Post Another Job
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
          <div>
            {step > 0 && step < 2 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="employer-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {step === 0 && (
              <Button variant="ghost" onClick={onComplete} className="text-slate-400" data-testid="employer-skip-btn">
                Skip for now
              </Button>
            )}
          </div>
          {step < 2 && (
            <Button onClick={nextStep} disabled={saving} data-testid="employer-next-btn">
              {saving ? 'Saving...' : step === 1 ? 'Post Job & Finish' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployerOnboarding;
